import { useCallback, useMemo } from "react";
import type { ZodType } from "zod";
import { navigate } from "@/utilities/navigate";
import { useLocation } from "@/hooks/useLocation";
import { stringify, parse } from "query-string";

const useSearchParams = <TModel>(
  schema: ZodType<TModel>,
  overrideBasePath = "",
) => {
  const { search, pathnameWithoutLocale } = useLocation();
  const parsedParams = useMemo(
    () => parse(search, { arrayFormat: "index" }),
    [search],
  );

  const basePath = overrideBasePath ? overrideBasePath : pathnameWithoutLocale;
  const result = schema.safeParse(parsedParams);
  const safeParams = result.success ? result.data : schema.parse({});

  const setSearchParams = useCallback(
    (params: Partial<TModel>) => {
      const mergedParams = stringify(
        {
          ...parsedParams,
          ...params,
        },
        { arrayFormat: "index" },
      );

      navigate(`${basePath}?${mergedParams}`);
    },
    [pathnameWithoutLocale, basePath, search],
  );

  const removeSearchParam = useCallback(
    (keyToRemove: keyof TModel) => {
      const filteredParams = stringify(
        Object.fromEntries(
          Object.entries(parsedParams).filter(([key]) => key != keyToRemove),
        ),
        { arrayFormat: "index" },
      );

      navigate(`${basePath}?${filteredParams}`);
    },
    [parsedParams, basePath, search],
  );

  return {
    setSearchParams,
    searchParams: safeParams,
    removeSearchParam,
  } as const;
};

export { useSearchParams };
